import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    RadioInput,
    redactPrivate,
    ScrollView,
    Shadows,
    Sheet,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';

interface AccountVerificationLetterSheetProps {
    isOpen: boolean;
    onClose: () => void;
    defaultBankAccountId?: string;
    isDownloading: boolean;
    onDownload: (account: BankAccountDisplayDetail) => void;
}

export const AccountVerificationLetterSheet = ({
    isOpen,
    onClose,
    defaultBankAccountId,
    isDownloading,
    onDownload,
}: AccountVerificationLetterSheetProps) => {
    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();

    const defaultAccountIndex = bankAccountDetails.findIndex(
        (d) => d.bankAccountId === defaultBankAccountId,
    );

    const [selectedAccountIndex, setSelectedAccountIndex] = useState(
        Math.max(defaultAccountIndex, 0),
    );

    useEffect(() => {
        if (isOpen) {
            setSelectedAccountIndex(Math.max(defaultAccountIndex, 0));
        }
    }, [isOpen, defaultAccountIndex]);

    const selectedAccount = bankAccountDetails[selectedAccountIndex];

    const handleClose = () => {
        if (!isDownloading) {
            onClose();
        }
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={handleClose}
            renderInAppRoot={false}
            disableDrag
            dismissOnOverlayPress={!isDownloading}
            title="Select account"
            showCloseButton={!isDownloading}
        >
            <YStack gap={16}>
                {isLoadingBankAccountDetails ? (
                    <Spinner />
                ) : (
                    <ScrollView
                        contentContainerStyle={{
                            paddingBottom: 8,
                        }}
                    >
                        <YStack
                            gap={8}
                            opacity={isDownloading ? 0.5 : 1}
                            pointerEvents={isDownloading ? 'none' : 'auto'}
                        >
                            {bankAccountDetails.map((account, index) => (
                                <XStack
                                    key={account.bankAccountId}
                                    padding={12}
                                    borderWidth={1}
                                    borderRadius={16}
                                    borderColor={
                                        index === selectedAccountIndex
                                            ? '$gray900'
                                            : '$gray400'
                                    }
                                    gap={12}
                                    alignItems="center"
                                    style={{ ...Shadows.SMALL }}
                                    onPress={() => {
                                        setSelectedAccountIndex(index);
                                    }}
                                    hoverStyle={{
                                        cursor: 'pointer',
                                        backgroundColor: '$gray100',
                                    }}
                                >
                                    <FruitfulCustomIcon
                                        icon={account.icon}
                                        color={account.color}
                                    />
                                    <YStack
                                        gap={4}
                                        flex={1}
                                        {...redactPrivate()}
                                    >
                                        <BodyMediumRegular>
                                            {account.nickname}
                                        </BodyMediumRegular>
                                        <BodyMediumRegular>
                                            Fruitful *
                                            {getLastFourAccountNumber(
                                                account.accountNumberMasked,
                                            )}
                                        </BodyMediumRegular>
                                    </YStack>
                                    <RadioInput
                                        isSelected={
                                            index === selectedAccountIndex
                                        }
                                        tone="neutral"
                                    />
                                </XStack>
                            ))}
                        </YStack>
                    </ScrollView>
                )}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text={
                        isDownloading
                            ? ''
                            : 'Download account verification letter'
                    }
                    icon={isDownloading ? <Spinner /> : null}
                    disabled={
                        isLoadingBankAccountDetails ||
                        selectedAccount == null ||
                        isDownloading
                    }
                    onPress={() => {
                        if (selectedAccount != null) {
                            onDownload(selectedAccount);
                        }
                    }}
                />
            </YStack>
        </Sheet>
    );
};
